<template>
  <div>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row  class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <div>
              <h1 class="header-title">
                <chevron
                  @click="back()"/>
                {{ $t('query.title') }}
              </h1>
              <div
                v-has-feature="'Explore'"
                class="header-add-button">
                <span
                  v-tooltip.left.top-start="$t('queries.createQuery')">
                  <font-awesome-icon
                    class="header-add-button-icon opacity"
                    @click="$router.push({name: 'ExplorerEvolutionVariables'})"
                    :icon="icon.add"/>
                </span>
              </div>
              <h2>{{ filterTitle }}</h2>
            </div>
            <alert
              v-if="alertVisible"
              :message="alertMessage"
              :type="alertType">
            </alert>
          </grid-col>
        </grid-row>
        <grid-row
          class="margin-top-xl"
          column-align="center">
          <grid-col
            col-size="4">
            <div
              style="display: flex; justify-content: right">
              <!--
               WARNING
               v-show="false"
              -->
              <tags-filter
                v-show="false"
                v-model="filterTags"
                :tagsTree="getTagsTreeState"/>
            </div>
          </grid-col>
        </grid-row>
        <grid-row  class="margin-top-l" column-align="center">
          <grid-col col-size="4">
            <query-data-table
              ref="queryDataTable"
              :fetchData="fetchData"
              v-on:onDelete="onDelete"
              v-on:onUpdate="onUpdate"
              v-on:onShare="onShare"
              v-on:onClickStar="onClickStar"
              v-on:onStoredQuery="onStoredQuery"/>
          </grid-col>
        </grid-row>
      </div>
      <ok-cancel-modal
        v-if="showConfirmationDeleteModal"
        v-on:ok="onDeleteConfirm"
        v-on:cancel="onDeleteCancel"
        :title="deleteModalMessage"
        :message="deleteModalMessage"
        :okButton="$t('commons.delete')"
        :cancelButton="$t('commons.cancel')">
        <div slot="body">
          <ul>
            <li>
              {{ queryToDelete.name }}
            </li>
          </ul>
        </div>
      </ok-cancel-modal>
    </main-layout>
  </div>
</template>

<script>
import MainLayout from '../MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import {mapActions, mapGetters} from 'vuex';
import pagination from '@/components/shared/table/pagination';
import Alert from '@/components/shared/alert/Alert';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import BtnPill from '@/components/shared/buttons/BtnPill';
import QueryDataTable from '@/components/query/QueryDataTable';
import Chevron from '@/components/shared/Chevron';
import TagsFilter from '@/components/variable/TagsFilter';
import {faPlusCircle} from '@fortawesome/pro-solid-svg-icons';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import navigation from '@/layouts/explorer/navigation';

export default {
  name: 'QueryList',
  components: {
    TagsFilter,
    MainLayout,
    GridRow,
    GridCol,
    OkCancelModal,
    Alert,
    BtnPill,
    QueryDataTable,
    Chevron,
    FontAwesomeIcon
  },
  data () {
    return {
      origin: 'Workspace',
      icon: {
        add: faPlusCircle
      },
      titleKey: '',
      queryToDelete: -1,
      showConfirmationDeleteModal: false,
      alertVisible: false,
      alertMessage: '',
      alertType: '', // error, warning, success
      deleteModalMessage: '',
      filter: undefined,
      filterTitle: '',
      tablePageSize: 10,
      tableState: {
        filter: '',
        page: 1,
        sort: {
          order: '',
          fieldName: ''
        }
      },
      breadcrumb: [
        {link: '/workspace', nameKey: 'menu.workspace'},
        {link: '/query/list', nameKey: 'query.title', disabled: true}
      ],
      filterTags: []
    };
  },
  watch: {
    getQueries: {
      handler: function () {
        this.$refs.queryDataTable.refresh();
      }
    },
    filterTags: {
      handler: function () {
        const query = this.filterTags.length === 0 ? {} : { tags: this.filterTags.join(',') };
        this.$router.push({ query: query });
        this.retrieveQueries({context: this, tags: this.filterTags});
      }
    }
  },
  created () {
    const origin = this.$route.query.origin;
    if (origin) {
      this.origin = origin;
    }

    const tags = this.$route.query.tags;
    if (tags) {
      this.filterTags = tags.split(',');
    }
    this.retrieveQueries({context: this, tags: this.filterTags});
    this.retrieveTagsTree({context: this});

    const alert = this.$route.params.alert;
    if (alert !== undefined) {
      this.showAlertMessage(this.$t(alert), 'success');
    }

    this.filter = this.$route.query.filter;
    switch (this.filter) {
      case navigation.FILTER_EXPLORER_EVOLUTION:
        this.filterTitle = this.$t('explorer.menu.evolution.label');
        break;

      case navigation.FILTER_EXPLORER_CORRELATION:
        this.filterTitle = this.$t('menu.correlation');
        break;

      case navigation.FILTER_EXPLORER_MULTIPLE_CORRELATION:
        this.filterTitle = this.$t('explorer.multipleCorrelation');
        break;
    }
  },
  computed: {
    ...mapGetters([
      'getQueries',
      'getTagsTreeState'
    ]),
    getBreadcrumb () {
      if (this.filter) {
        switch (this.filter) {
          case navigation.FILTER_EXPLORER_EVOLUTION:
            return [
              {name: this.$t('explorer.breadcrumbParent'), link: '/explore'},
              {name: this.$t('explorer.parameterSelection'), link: '/explorer-evolution-variables'},
              {name: this.$t('query.title'), link: '/query/list', disabled: true}
            ];

          case navigation.FILTER_EXPLORER_CORRELATION:
            return [
              {name: this.$t('explorer.breadcrumbParentOldExplorer'), link: '/explore'},
              {name: this.$t('explorer.parameterSelection'), link: '/explorer-correlation-variables'},
              {name: this.$t('query.title'), link: '/query/list', disabled: true}
            ];

          case navigation.FILTER_EXPLORER_MULTIPLE_CORRELATION:
            return [
              {name: this.$t('explorer.breadcrumbMultipleCorrelation'), link: '/explore'},
              {name: this.$t('explorer.parameterSelection'), link: '/explorer-multiple-correlation-variables'},
              {name: this.$t('query.title'), link: '/query/list', disabled: true}
            ];
        }
      } else {
        // Default breadcrumb
        return this.breadcrumb.map(route => {
          route.name = this.$t(route.nameKey);
          return route;
        });
      }
    }
  },
  methods: {
    ...mapActions([
      'retrieveQueries',
      'retrieveTagsTree',
      'updateQuery',
      'deleteQuery'
    ]),
    back () {
      if (this.filter) {
        switch (this.filter) {
          case navigation.FILTER_EXPLORER_EVOLUTION:
            this.$router.push({name: 'ExplorerEvolutionVariables'});
            break;
          case navigation.FILTER_EXPLORER_CORRELATION:
            this.$router.push({name: 'ExplorerCorrelationVariables'});
            break;
          case navigation.FILTER_EXPLORER_MULTIPLE_CORRELATION:
            this.$router.push({name: 'ExplorerMultipleCorrelationVariables'});
            break;
        }
      } else {
        this.$router.push({name: this.origin});
      }
    },
    onStoredQuery (path) {
      const filterParam = this.filter ? '&filter=' + this.filter : '';
      this.$router.push(path + filterParam + '&origin=' + this.$options.name);
    },
    onUpdate (query) {
      this.$router.push({ name: 'Query', params: query });
    },
    onDelete (query) {
      this.queryToDelete = query;
      this.deleteModalMessage = this.$t('query.deleteModalMessage');
      this.showConfirmationDeleteModal = true;
    },
    onDeleteConfirm () {
      const id = this.queryToDelete.id;
      const queryName = this.queryToDelete.name;
      this.queryToDelete = {};
      this.showConfirmationDeleteModal = false;
      this.deleteQuery({ context: this, id: id }).then(() => {
        this.showAlertMessage(this.$t('query.successDelete', {name: queryName}), 'success');
      }, () => {
        this.showAlertMessage(this.$t('query.errorDelete', {name: queryName}), 'error');
      });
    },
    onDeleteCancel () {
      this.queryToDelete = {};
      this.showConfirmationDeleteModal = false;
    },
    onShare (query) {
      this.$router.push({
        name: 'Permissions',
        params: {
          titleKey: 'permission.titleQuery',
          type: 'query',
          id: query.id,
          breadcrumb: this.breadcrumb
        }});
    },
    onClickStar (query) {
      if (query.mine === true) {
        query.star = !query.star;
        this.updateQuery({context: this, query});
      }
    },
    getFilteredQueries () {
      if (this.filter) {
        return this.getQueries.filter(query => query.relativePath.includes(this.filter));
      } else {
        return this.getQueries;
      }
    },
    fetchData ({ page, filter, sort }) {
      const tablePage = pagination.createPage(
        this.getFilteredQueries(),
        this.tableState,
        ['name'],
        this.tablePageSize,
        { page, filter, sort });
      this.tableState = tablePage.state;
      return tablePage.result;
    },
    starClass (query) {
      return query.mine ? 'table-iconset-icon' : 'table-icon-disabled';
    },
    showAlertMessage (message, type) {
      this.alertVisible = true;
      this.alertMessage = message;
      this.alertType = type;
      this.showAlert = true;

      setTimeout(() => {
        this.alertVisible = false;
      }, 2000);
    }
  }
};
</script>

<style lang="scss" scoped>
  @import "src/stylesheets/variables";

  .header-title {
    float: left;
  }

  h2 {
    line-height: 2.2;
    font-size: 1.2rem;
    font-weight: lighter;
    margin-bottom: 0;
    margin-left: $sp-margin;
    padding-bottom: 0;
    padding-left: $sp-margin;
    border-left: 1px solid $black-trans6;
    float: left;
  }

  .header-add-button {
    float: right;
    cursor: pointer;
  }

  .header-add-button-icon {
    font-size: 3rem;
    color: $secondary;
  }

  .opacity:hover {
    opacity: 0.3;
    transition: opacity 0.3s;
    cursor: pointer;
  }

</style>



// WEBPACK FOOTER //
// src/layouts/queries/QueryList.vue